/** 30-Day Summary prefill field definitions and form application helpers. */

import type { UseFormSetValue } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import type { ThirtyDaySummaryPrefillResponse } from '../services/api/types.gen';
import {
  getThirtyDaySummarySections,
  VITALS_GRID_COLUMNS,
  VITALS_GRID_ROWS,
} from '../components/patient/thirtyDaySummaryConfig';

export const PREFILL_FIELD_KEYS = [
  'homebound_status',
  'homebound_status_other',
  'patient_condition',
  'services_provided',
  'vital_sign_ranges',
  'summary_of_care_provided',
  'current_condition',
  'goals',
  'recommended_services',
  'recommended_services_other',
] as const;

export type ThirtyDaySummaryPrefillField = (typeof PREFILL_FIELD_KEYS)[number];

function hasNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasNonEmptyStringArray(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.some(item => typeof item === 'string' && item.trim().length > 0)
  );
}

function hasVitalsGrid(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  for (const row of VITALS_GRID_ROWS) {
    const rowData = (value as Record<string, unknown>)[row];
    if (!rowData || typeof rowData !== 'object') continue;
    for (const col of VITALS_GRID_COLUMNS) {
      const cell = (rowData as Record<string, unknown>)[col.key];
      if (typeof cell === 'string' && cell.trim()) return true;
    }
  }
  return false;
}

export function hasThirtyDaySummaryPrefillContent(
  values: FieldValues
): boolean {
  for (const key of PREFILL_FIELD_KEYS) {
    const value = values[key];
    if (key === 'vital_sign_ranges') {
      if (hasVitalsGrid(value)) return true;
      continue;
    }
    if (hasNonEmptyStringArray(value) || hasNonEmptyString(value)) {
      return true;
    }
  }
  return false;
}

export function clearThirtyDaySummaryPrefillFields(
  setValue: UseFormSetValue<FieldValues>
): void {
  for (const section of getThirtyDaySummarySections()) {
    switch (section.kind) {
      case 'textarea':
      case 'single-select':
        setValue(section.key, '', { shouldDirty: true });
        break;
      case 'checkbox-group':
        setValue(section.key, [], { shouldDirty: true });
        if (section.allowOther) {
          setValue(`${section.key}_other`, '', { shouldDirty: true });
        }
        break;
      case 'vitals-grid':
        setValue(section.key, {}, { shouldDirty: true });
        break;
    }
  }
}

export function applyThirtyDaySummaryPrefill(
  setValue: UseFormSetValue<FieldValues>,
  data: ThirtyDaySummaryPrefillResponse,
  clearFirst: boolean
): void {
  if (clearFirst) {
    clearThirtyDaySummaryPrefillFields(setValue);
  }

  for (const key of PREFILL_FIELD_KEYS) {
    const value = data[key as keyof ThirtyDaySummaryPrefillResponse];
    if (value === null || value === undefined) continue;
    if (key === 'vital_sign_ranges') {
      if (hasVitalsGrid(value)) {
        setValue(key, value, { shouldDirty: true });
      }
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length > 0) {
        setValue(key, value, { shouldDirty: true });
      }
      continue;
    }
    if (typeof value === 'string' && value.trim()) {
      setValue(key, value, { shouldDirty: true });
    }
  }
}
